import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Copy,
  Save,
  Trash2,
  Search,
  Download,
  Loader2,
  CheckCircle2,
  CircleAlert,
  KeyRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Iter91 — Central Delivery Man Logins page.
 *
 * One screen to view / set / update / export every Indane agency's
 * delivery-man credentials. Admin-only. Mirrors the per-agency 🔐 Login
 * tab inside the Delivery Dashboard — both paths hit the same backend
 * endpoints so the data stays in sync.
 */
export default function DeliveryLogins() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "set" | "pending"
  const [edits, setEdits] = useState({}); // group_id -> {username,password,showPassword,saving,editingPassword}
  const passwordRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/delivery-credentials");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const set = rows.filter((r) => r.is_set).length;
    return { total, set, pending: total - set };
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "set" && !r.is_set) return false;
      if (filter === "pending" && r.is_set) return false;
      if (q && !(r.agency_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, query, filter]);

  const bindEdit = (gid, patch) => {
    setEdits((prev) => ({
      ...prev,
      [gid]: { ...(prev[gid] || {}), ...patch },
    }));
  };

  const effective = (r) => {
    const e = edits[r.group_id] || {};
    return {
      username: e.username !== undefined ? e.username : r.username,
      password: e.password !== undefined ? e.password : r.password,
      showPassword: !!e.showPassword,
      saving: !!e.saving,
      editingPassword: !!e.editingPassword,
      dirty:
        (e.username !== undefined && e.username !== r.username) ||
        (e.password !== undefined && e.password !== r.password),
    };
  };

  // Iter92: explicit "Change Password" entry point for saved rows.
  // Locks the fields read-only until the admin clicks 🔑 Change — then
  // the password input is cleared, focused, and the Save/Cancel buttons
  // replace the key button. Keeps username as-is per user ask
  // ("user id same hi rahega").
  const beginPasswordEdit = (r) => {
    setEdits((prev) => ({
      ...prev,
      [r.group_id]: {
        ...(prev[r.group_id] || {}),
        editingPassword: true,
        password: "",
        showPassword: false,
      },
    }));
    // Focus after state flush
    setTimeout(() => {
      const el = passwordRefs.current[r.group_id];
      if (el) {
        el.focus();
        el.select();
      }
    }, 50);
  };

  const cancelPasswordEdit = (r) => {
    setEdits((prev) => {
      const nxt = { ...prev };
      delete nxt[r.group_id];
      return nxt;
    });
  };

  const handleSave = async (r) => {
    const eff = effective(r);
    const u = eff.username.trim();
    const p = eff.password.trim();
    if (!u || !p) {
      toast.error("Username aur password dono fill karo");
      return;
    }
    bindEdit(r.group_id, { saving: true });
    try {
      await api.put(`/delivery-credentials/${r.group_id}`, {
        username: u,
        password: p,
      });
      toast.success(`${r.agency_name} saved ✓`);
      // Clear the edit buffer so new row data wins
      setEdits((prev) => {
        const nxt = { ...prev };
        delete nxt[r.group_id];
        return nxt;
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      bindEdit(r.group_id, { saving: false });
    }
  };

  const handleDelete = async (r) => {
    if (
      !window.confirm(
        `${r.agency_name} ke delivery man credentials delete karni hain? Undo nahi hoga.`,
      )
    )
      return;
    bindEdit(r.group_id, { saving: true });
    try {
      await api.delete(`/delivery-credentials/${r.group_id}`);
      toast.success(`${r.agency_name} cleared`);
      setEdits((prev) => {
        const nxt = { ...prev };
        delete nxt[r.group_id];
        return nxt;
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      bindEdit(r.group_id, { saving: false });
    }
  };

  const copyValue = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const exportCSV = () => {
    const setRows = rows.filter((r) => r.is_set);
    if (setRows.length === 0) {
      toast.error("Koi saved credentials nahi hain");
      return;
    }
    const header = ["Agency", "Username", "Password", "Last Updated", "Updated By"];
    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
    const body = setRows
      .map((r) =>
        [r.agency_name, r.username, r.password, r.updated_at, r.updated_by]
          .map(esc)
          .join(","),
      )
      .join("\n");
    const csv = header.map(esc).join(",") + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `delivery-logins-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${setRows.length} rows exported`);
  };

  return (
    <div className="min-h-screen bg-[#EFEEE6] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              data-testid="delivery-logins-back"
              className="p-2 rounded hover:bg-white/60"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5 text-[#1C1C19]" />
            </button>
            <KeyRound className="w-6 h-6 text-[#385C4B]" />
            <div>
              <h1 className="text-xl font-bold text-[#1C1C19] leading-tight">
                Delivery Man Logins
              </h1>
              <p className="text-xs text-[#6B6B61] font-mono">
                Indane agencies ki delivery-man credentials — one page
              </p>
            </div>
          </div>
          <button
            onClick={exportCSV}
            data-testid="delivery-logins-export"
            className="px-3 py-1.5 text-xs font-semibold border border-[#DCDCD3] bg-white text-[#1C1C19] rounded hover:bg-[#F7F6EE] flex items-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>

        <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-[#DCDCD3] bg-[#F7F6EE] flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white border border-[#DCDCD3] rounded px-2 py-1.5 min-w-[220px] flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 text-[#6B6B61]" />
              <input
                type="text"
                placeholder="Search agency name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="delivery-logins-search"
                className="flex-1 text-xs font-mono bg-transparent focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1">
              {[
                ["all", `All ${stats.total}`],
                ["set", `Set ${stats.set}`],
                ["pending", `Pending ${stats.pending}`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  data-testid={`delivery-logins-filter-${key}`}
                  className={`px-2.5 py-1 text-[11px] font-mono rounded ${
                    filter === key
                      ? "bg-[#385C4B] text-white"
                      : "bg-white border border-[#DCDCD3] text-[#1C1C19] hover:bg-[#EFEEE6]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-[11px] font-mono text-[#6B6B61] tabular-nums">
              Showing <b>{visibleRows.length}</b> row{visibleRows.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="py-12 text-center text-xs font-mono text-[#6B6B61]">
              Koi row match nahi — filter / search change karo
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="delivery-logins-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F7F6EE] border-b border-[#DCDCD3]">
                    <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-wider text-[#6B6B61] w-10">
                      #
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-wider text-[#6B6B61]">
                      Agency
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-wider text-[#6B6B61]">
                      Username
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-wider text-[#6B6B61]">
                      Password
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-mono uppercase tracking-wider text-[#6B6B61] w-32">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => {
                    const eff = effective(r);
                    const editing = eff.editingPassword;
                    return (
                      <React.Fragment key={r.group_id}>
                      <tr
                        className={`border-b border-[#EFEEE6] ${
                          r.is_set
                            ? editing
                              ? "bg-[#FBF9F2]"
                              : "bg-white"
                            : "bg-[#FFFCF2]"
                        }`}
                        data-testid={`delivery-login-row-${r.group_id}`}
                      >
                        <td className="px-3 py-2 text-[11px] font-mono text-[#6B6B61] tabular-nums">
                          {i + 1}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {r.is_set ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-[#385C4B] flex-shrink-0" />
                            ) : (
                              <CircleAlert className="w-3.5 h-3.5 text-[#B8923B] flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <span className="text-xs font-semibold text-[#1C1C19]">
                                {r.agency_name}
                              </span>
                              {r.type === "user" && (
                                <span
                                  className="ml-2 text-[9px] font-mono uppercase tracking-wider bg-[#8A6046]/10 text-[#8A6046] px-1.5 py-0.5 rounded align-middle"
                                  title="User-based agency (no chat group) — credentials still stored per Delivery Logins"
                                  data-testid={`delivery-login-type-user-${r.group_id}`}
                                >
                                  👤 User
                                </span>
                              )}
                              {r.user_handle && (
                                <div className="text-[10px] font-mono text-[#6B6B61] mt-0.5">
                                  @{r.user_handle}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {r.is_set ? (
                          <>
                            {/* Iter93: saved row renders as read-only text —
                                the 🔑 icon opens an inline edit panel below
                                this row (<tr colSpan=5>) so both User ID
                                and Password can be updated together. */}
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <span
                                  className="text-xs font-mono text-[#1C1C19]"
                                  data-testid={`delivery-login-username-${r.group_id}`}
                                >
                                  {r.username}
                                </span>
                                <button
                                  onClick={() => copyValue(r.username, "Username")}
                                  className="p-1 hover:bg-[#F7F6EE] rounded"
                                  title="Copy"
                                >
                                  <Copy className="w-3 h-3 text-[#6B6B61]" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <span
                                  className="text-xs font-mono text-[#1C1C19] tracking-wider"
                                  data-testid={`delivery-login-password-${r.group_id}`}
                                >
                                  {eff.showPassword
                                    ? r.password
                                    : "•".repeat(Math.min(10, r.password.length || 6))}
                                </span>
                                <button
                                  onClick={() =>
                                    bindEdit(r.group_id, {
                                      showPassword: !eff.showPassword,
                                    })
                                  }
                                  className="p-1 hover:bg-[#F7F6EE] rounded"
                                  title={eff.showPassword ? "Hide" : "Show"}
                                >
                                  {eff.showPassword ? (
                                    <EyeOff className="w-3 h-3 text-[#6B6B61]" />
                                  ) : (
                                    <Eye className="w-3 h-3 text-[#6B6B61]" />
                                  )}
                                </button>
                                <button
                                  onClick={() => copyValue(r.password, "Password")}
                                  className="p-1 hover:bg-[#F7F6EE] rounded"
                                  title="Copy"
                                >
                                  <Copy className="w-3 h-3 text-[#6B6B61]" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                {!editing && (
                                  <button
                                    onClick={() => beginPasswordEdit(r)}
                                    data-testid={`delivery-login-change-${r.group_id}`}
                                    className="p-1.5 bg-[#8A6046] text-white rounded hover:bg-[#6E4A34] flex items-center justify-center"
                                    title="Change credentials"
                                  >
                                    <KeyRound className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(r)}
                                  disabled={eff.saving || editing}
                                  data-testid={`delivery-login-delete-${r.group_id}`}
                                  className="p-1.5 border border-[#DCDCD3] text-[#8A6046] rounded hover:bg-[#F7F6EE] disabled:opacity-40"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            {/* Pending row — original first-time setup flow:
                                both fields editable inline + single Save. */}
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={eff.username}
                                  onChange={(e) =>
                                    bindEdit(r.group_id, { username: e.target.value })
                                  }
                                  placeholder="username"
                                  data-testid={`delivery-login-username-${r.group_id}`}
                                  className="flex-1 px-2 py-1 text-xs font-mono border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <input
                                  type={eff.showPassword ? "text" : "password"}
                                  value={eff.password}
                                  onChange={(e) =>
                                    bindEdit(r.group_id, { password: e.target.value })
                                  }
                                  placeholder="password"
                                  data-testid={`delivery-login-password-${r.group_id}`}
                                  className="flex-1 px-2 py-1 text-xs font-mono border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
                                />
                                {eff.password && (
                                  <button
                                    onClick={() =>
                                      bindEdit(r.group_id, {
                                        showPassword: !eff.showPassword,
                                      })
                                    }
                                    className="p-1 hover:bg-[#F7F6EE] rounded"
                                    title={eff.showPassword ? "Hide" : "Show"}
                                  >
                                    {eff.showPassword ? (
                                      <EyeOff className="w-3 h-3 text-[#6B6B61]" />
                                    ) : (
                                      <Eye className="w-3 h-3 text-[#6B6B61]" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleSave(r)}
                                  disabled={
                                    eff.saving ||
                                    !eff.username.trim() ||
                                    !eff.password.trim()
                                  }
                                  data-testid={`delivery-login-save-${r.group_id}`}
                                  className="px-2 py-1 text-[11px] font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] disabled:opacity-40 flex items-center gap-1"
                                >
                                  {eff.saving ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Save className="w-3 h-3" />
                                  )}
                                  Save
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                      {editing && r.is_set && (
                        <tr
                          className="bg-[#FBF9F2] border-b border-[#EFEEE6]"
                          data-testid={`delivery-login-edit-panel-${r.group_id}`}
                        >
                          <td colSpan={5} className="px-5 pt-3 pb-4">
                            <div className="rounded-md border border-[#DCDCD3] bg-white p-4">
                              <h4 className="text-xs font-semibold text-[#1C1C19] mb-3 flex items-center gap-1.5">
                                <KeyRound className="w-3.5 h-3.5 text-[#8A6046]" />
                                Update credentials · {r.agency_name}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <label className="block">
                                  <span className="block text-[10px] font-mono uppercase tracking-wider text-[#6B6B61] mb-1">
                                    User ID *
                                  </span>
                                  <input
                                    type="text"
                                    value={eff.username}
                                    onChange={(e) =>
                                      bindEdit(r.group_id, {
                                        username: e.target.value,
                                      })
                                    }
                                    placeholder="user id"
                                    data-testid={`delivery-login-edit-username-${r.group_id}`}
                                    className="w-full px-3 py-2 text-sm font-mono border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
                                  />
                                </label>
                                <label className="block">
                                  <span className="block text-[10px] font-mono uppercase tracking-wider text-[#6B6B61] mb-1">
                                    Password *
                                  </span>
                                  <input
                                    ref={(el) => {
                                      passwordRefs.current[r.group_id] = el;
                                    }}
                                    type={eff.showPassword ? "text" : "password"}
                                    value={eff.password}
                                    onChange={(e) =>
                                      bindEdit(r.group_id, {
                                        password: e.target.value,
                                      })
                                    }
                                    placeholder="Naya password type karo"
                                    data-testid={`delivery-login-edit-password-${r.group_id}`}
                                    className="w-full px-3 py-2 text-sm font-mono border border-[#385C4B] rounded bg-white ring-2 ring-[#385C4B]/20 focus:outline-none"
                                  />
                                </label>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleSave(r)}
                                  disabled={
                                    eff.saving ||
                                    !eff.username.trim() ||
                                    !eff.password.trim()
                                  }
                                  data-testid={`delivery-login-update-${r.group_id}`}
                                  className="px-3 py-1.5 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] disabled:opacity-40 flex items-center gap-1.5"
                                >
                                  {eff.saving ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Save className="w-3.5 h-3.5" />
                                  )}
                                  Update credentials
                                </button>
                                <button
                                  onClick={() => cancelPasswordEdit(r)}
                                  disabled={eff.saving}
                                  data-testid={`delivery-login-cancel-${r.group_id}`}
                                  className="px-3 py-1.5 text-xs font-semibold border border-[#DCDCD3] text-[#1C1C19] bg-white rounded hover:bg-[#F7F6EE] disabled:opacity-40"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] font-mono text-[#6B6B61] mt-3 text-center">
          Credentials sirf admin ke liye reference hain. Delivery dashboard ke
          🔐 Login tab se bhi same credentials edit ho sakte hain.
        </p>
      </div>
    </div>
  );
}
